import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import Container from "@mui/material/Container";
import Logo from "../shared/logo";
import { Link, useLocation } from "react-router-dom";
import { useState, MouseEvent } from "react";
import { subscribeToPush } from "../pages/chat/client";
import { notificationsCapable } from "../app/App";

/* ResponsiveMenuProps example
 * const mainMenu = [
 *   { menuitem: "Главная", link: "/" },
 *   { menuitem: "Экзамены", link: "/exam" },
 *   { menuitem: "Олимпиады", link: "/theme" },
 *   { menuitem: "Обучение", link: "/learning" },
 *   {
 *     menuitem: "Отчеты",
 *     submenu: [
 *       { menuitem: "Ответы", link: "/answers" },
 *       { menuitem: "Домашние задания (25)", link: "/homework" },
 *     ],
 *   },
 * ];
 * const rightMenu = [
 *     menuitem: "Id_999",
 *     submenu: [
 *          { menuitem: "Мой профиль", link: "/account" },
 *          { menuitem: "Выйти", link: "/logout" },
 *      ]
 */

export interface ResponsiveMenuItem {
  menuitem: string;
  link?: string;
  data?: string; // for display red dot notification
  submenu?: {
    menuitem: string;
    link: string;
  }[];
}

export interface ResponsiveMenuProps {
  mainMenu: ResponsiveMenuItem[];
  rightMenu: ResponsiveMenuItem;
}

function isTheSameUrl(base: string, url: string): boolean {
  if (base.length === url.length && base === url) {
    return true;
  } else if (
    base.length > 1 &&
    base.length < url.length &&
    url.startsWith(base)
  ) {
    return true;
  }
  return false;
}

// TODO make popups as a component
function ResponsiveMenu(props: ResponsiveMenuProps) {
  const { mainMenu, rightMenu } = props;
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [anchorElReports, setAnchorElReports] = useState<null | HTMLElement>(
    null,
  );
  const [anchorForPopup, setAnchorForPopup] = useState<null | HTMLElement>(
    null,
  );
  const location = useLocation();

  const handleOpenNavMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
    if (notificationsCapable) {
      subscribeToPush()
        .then((res) => {
          console.log("Subscribed to push notifications:", res);
        })
        .catch((error) => {
          console.error("Failed to subscribe to push notifications:", error);
        });
    }
  };
  const handleOpenReportsMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorElReports(event.currentTarget);
    setAnchorForPopup(event.currentTarget);
  };

  const setPopupInactive = () => {
    if (anchorForPopup) anchorForPopup.ariaCurrent = null;
  };

  const setPopupActive = () => {
    if (anchorForPopup) anchorForPopup.ariaCurrent = "page";
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };
  const handleCloseReportsMenu = () => {
    setAnchorElReports(null);
  };

  return (
    <AppBar position="static" className="ResponsiveMenuBar">
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <Typography
            key="t1"
            variant="h6"
            noWrap
            component="div"
            sx={{
              mr: 2,
              display: { xs: "none", md: "flex" },
              fontFamily: "monospace",
              fontWeight: 700,
              letterSpacing: ".3rem",
              color: "inherit",
              textDecoration: "none",
            }}
          >
            <Logo />
          </Typography>
          {/* Full size ===================================================== */}
          <Box
            key="fullsizemenubox"
            sx={{
              flexGrow: 1,
              display: {
                xs: "none",
                md: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
            }}
          >
            {mainMenu.map((page, i) => {
              if (page.link) {
                return (
                  <Link
                    className="RMLink"
                    key={page.menuitem + i}
                    onClick={() => {
                      handleCloseNavMenu();
                      setPopupInactive();
                    }}
                    aria-current={
                      isTheSameUrl(page.link, location.pathname)
                        ? "page"
                        : undefined
                    }
                    to={page.link}
                  >
                    {page.menuitem}
                  </Link>
                );
              } else {
                // submenu ------------------------------
                return (
                  <Box key={"submenu" + i} sx={{ flexGrow: 0 }}>
                    <Link
                      className="RMLink"
                      key="reportsmenu"
                      onClick={handleOpenReportsMenu}
                      to="#"
                    >
                      {page.menuitem}
                    </Link>
                    <Menu
                      sx={{ mt: "45px" }}
                      className="RMPopupMenu"
                      id="menu-appbar"
                      anchorEl={anchorElReports}
                      anchorOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                      keepMounted
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                      open={Boolean(anchorElReports)}
                      onClose={handleCloseReportsMenu}
                    >
                      {page.submenu!.map((s) => (
                        <Link
                          className="RMLinkSmall"
                          aria-current={
                            isTheSameUrl(s.link, location.pathname)
                              ? "page"
                              : undefined
                          }
                          key={s.menuitem}
                          onClick={() => {
                            handleCloseReportsMenu();
                            setPopupActive();
                          }}
                          to={s.link}
                        >
                          {s.menuitem}
                        </Link>
                      ))}
                    </Menu>
                  </Box>
                );
              }
            })}
          </Box>

          {/* Mobile size ===================================================== */}
          <Box
            key="mobilebox"
            sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}
          >
            <IconButton
              size="large"
              aria-label="hamburger menu"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              key="mobileboxpopup"
              className="RMPopupMenu"
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{ display: { xs: "block", md: "none" } }}
            >
              {mainMenu.map((page, i) => {
                if (page.link) {
                  return (
                    <Link
                      className="RMLinkSmall"
                      key={page.menuitem + i}
                      onClick={() => {
                        handleCloseNavMenu();
                        setPopupInactive();
                      }}
                      aria-current={
                        isTheSameUrl(
                          page.link ? page.link : "qq",
                          location.pathname,
                        )
                          ? "page"
                          : undefined
                      }
                      to={page.link!}
                    >
                      {page.menuitem}
                    </Link>
                  );
                } else {
                  // mobile submenu ------------------------------
                  return (
                    <Box key={"box" + page.menuitem + i} sx={{ flexGrow: 0 }}>
                      <Link
                        className="RMLinkSmall"
                        key={"submenulink" + page.menuitem + i}
                        onClick={handleOpenReportsMenu}
                        to="#"
                      >
                        {page.menuitem}
                      </Link>
                      <Menu
                        key={"submenu" + page.menuitem + i}
                        sx={{ mt: "45px" }}
                        className="RMPopupMenu"
                        id="menu-appbar"
                        anchorEl={anchorElReports}
                        anchorOrigin={{
                          vertical: "top",
                          horizontal: "right",
                        }}
                        keepMounted
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "right",
                        }}
                        open={Boolean(anchorElReports)}
                        onClose={handleCloseReportsMenu}
                      >
                        {page.submenu!.map((s, i) => (
                          <Link
                            className="RMLinkSmall"
                            aria-current={
                              isTheSameUrl(s.link, location.pathname)
                                ? "page"
                                : undefined
                            }
                            key={s.menuitem + i}
                            onClick={() => {
                              handleCloseReportsMenu();
                              handleCloseNavMenu();
                              setPopupActive();
                            }}
                            to={s.link}
                          >
                            {s.menuitem}
                          </Link>
                        ))}
                      </Menu>
                    </Box>
                  );
                }
              })}
            </Menu>
          </Box>
          <Typography
            key="t2"
            variant="h5"
            noWrap
            component="div"
            sx={{
              mr: 2,
              display: { xs: "flex", md: "none" },
              flexGrow: 1,
              fontFamily: "monospace",
              fontWeight: 700,
              letterSpacing: ".3rem",
              color: "inherit",
              textDecoration: "none",
            }}
          >
            <Logo />
          </Typography>
          {/* Right menu ===================================================== */}
          <Box key="rightmenubox" sx={{ flexGrow: 0 }}>
            {rightMenu.link ? (
              <Link
                className="RMLink"
                key={rightMenu.menuitem}
                onClick={() => {
                  handleCloseNavMenu();
                  setPopupInactive();
                }}
                aria-current={
                  isTheSameUrl(rightMenu.link, location.pathname)
                    ? "page"
                    : undefined
                }
                to={rightMenu.link}
              >
                {rightMenu.menuitem}
              </Link>
            ) : (
              <>
                <Link
                  className="RMLink"
                  key="rightmenu"
                  onClick={handleOpenUserMenu}
                  to="#"
                >
                  <span className={rightMenu.data}>{rightMenu.menuitem}</span>
                </Link>
                <Menu
                  sx={{ mt: "45px" }}
                  className="RMPopupMenu"
                  id="menu-appbar"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                >
                  {rightMenu.submenu!.map((s) => (
                    <Link
                      className="RMLinkSmall"
                      aria-current={
                        isTheSameUrl(s.link, location.pathname)
                          ? "page"
                          : undefined
                      }
                      key={s.menuitem}
                      onClick={() => {
                        handleCloseUserMenu();
                        setPopupInactive();
                      }}
                      to={s.link}
                    >
                      {s.menuitem}
                    </Link>
                  ))}
                </Menu>
              </>
            )}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}
export default ResponsiveMenu;
